// TableFormView.jsx — table + field + access-control builder
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Table2,
  Layers,
  Shield,
  CircleCheck,
  Check,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Save,
  Loader2,
  Copy,
  Download,
  ArrowUp,
  ArrowDown,
  Link2,
  Hash,
  CalendarDays,
  ToggleLeft,
  List as ListIcon,
  Type as TypeIcon,
  AlignLeft,
  Eye,
  Pencil,
  Info,
  Database,
  KeyRound,
  Sparkles,
  Compass,
  FolderPlus,
  MoveVertical,
} from "lucide-react";
import { moduleIcon } from "../components/NavigationView";

const FIELD_TYPE_OPTIONS = [
  { label: "Text (Short)", value: "VARCHAR(255)" },
  { label: "Text (Long)", value: "TEXT" },
  { label: "Number (Integer)", value: "INTEGER" },
  { label: "Number (Decimal)", value: "NUMERIC" },
  { label: "Boolean", value: "BOOLEAN" },
  { label: "Date", value: "DATE" },
  { label: "Date & Time", value: "TIMESTAMP" },
  { label: "UUID (Reference)", value: "UUID" },
  { label: "Choice (single)", value: "ENUM" },
];

const METHOD_DEFS = [
  { key: "GET", label: "View", icon: Eye },
  { key: "POST", label: "Create", icon: Plus },
  { key: "PUT", label: "Edit", icon: Pencil },
  { key: "DELETE", label: "Delete", icon: Trash2 },
];

const WIZARD_STEPS = [
  { key: "basics", label: "Basics", icon: Table2 },
  { key: "fields", label: "Fields", icon: Layers },
  { key: "access", label: "Access", icon: Shield },
  { key: "navigation", label: "Navigation", icon: Compass },
  { key: "review", label: "Review", icon: CircleCheck },
];

const EDIT_TABS = [
  { key: "fields", label: "Fields", icon: Layers },
  { key: "access", label: "Access Control", icon: Shield },
  { key: "navigation", label: "Navigation", icon: Compass },
];

const isEnum = (ft) => (ft || "").toUpperCase() === "ENUM";
const isUUID = (ft) => (ft || "").toUpperCase() === "UUID";
const isVarchar = (ft) => /^VARCHAR\(\d+\)$/i.test(ft || "");

const parseVarcharLen = (ft) => {
  const m = /^VARCHAR\((\d+)\)$/i.exec(ft || "");
  return m ? Number(m[1]) : 255;
};

const coerceDefaultByType = (fieldType, rawVal) => {
  const t = (fieldType || "").toUpperCase();
  if (t === "BOOLEAN") return !!rawVal;
  if (t === "INTEGER") return rawVal === "" || rawVal === null ? "" : String(parseInt(rawVal, 10));
  if (t === "NUMERIC") return rawVal === "" || rawVal === null ? "" : String(Number(rawVal));
  return rawVal ?? ""; // keep strings for DATE/TIMESTAMP/TEXT/UUID/ENUM
};

const slugify = (s) =>
  String(s).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

const fieldTypeIcon = (fieldType) => {
  const t = (fieldType || "").toUpperCase();
  if (t === "UUID") return Link2;
  if (t === "ENUM") return ListIcon;
  if (t === "BOOLEAN") return ToggleLeft;
  if (t === "DATE" || t === "TIMESTAMP") return CalendarDays;
  if (t === "INTEGER" || t === "NUMERIC") return Hash;
  if (t === "TEXT") return AlignLeft;
  return TypeIcon;
};

const idOf = (v) => ((v && typeof v === "object" ? v.id : v) || "");

const TableFormView = () => {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const [table, setTable] = useState({ name: "", label: "" });
  const [fields, setFields] = useState([]);
  const [originalFields, setOriginalFields] = useState([]);
  const [deletedFieldIds, setDeletedFieldIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [tablesOptions, setTablesOptions] = useState([]);
  const [expandedAdvanced, setExpandedAdvanced] = useState({});
  const [errors, setErrors] = useState([]);
  const [csvOpenIdx, setCsvOpenIdx] = useState(null);
  const [csvDraft, setCsvDraft] = useState({});

  const [step, setStep] = useState("basics"); // wizard (new table)
  const [activeTab, setActiveTab] = useState("fields"); // tabs (edit table)

  const [roles, setRoles] = useState([]);
  const [aclRules, setAclRules] = useState([]);
  const [savingRuleKey, setSavingRuleKey] = useState(null);

  const [navGroups, setNavGroups] = useState([]);
  const [navModulesByGroup, setNavModulesByGroup] = useState({});
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [navLabel, setNavLabel] = useState("");
  const [navPosition, setNavPosition] = useState(0);
  const [existingNavModuleId, setExistingNavModuleId] = useState(null);
  const [newGroupMode, setNewGroupMode] = useState(false);
  const [newGroupLabel, setNewGroupLabel] = useState("");
  const [savingNav, setSavingNav] = useState(false);
  const [navPlacementLoaded, setNavPlacementLoaded] = useState(false);

  const isNew = recordId === "new";
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // Load ALL available tables for the Reference Table dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${apiBaseUrl}/search/tables/`, auth);
        const records = res?.data?.records || [];
        setTablesOptions(records.map((t) => ({ id: t.id, name: t.name, label: t.label })));
      } catch (e) {
        console.error("Failed to load list of tables:", e);
      }
    })();
  }, [apiBaseUrl, auth]);

  // Load roles for the Access Control step/tab
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${apiBaseUrl}/search/roles/?limit=1000&sort_by=name&sort_order=asc`, auth);
        setRoles(res.data?.records || []);
      } catch (e) {
        console.error("Failed to load roles:", e);
      }
    })();
  }, [apiBaseUrl, auth]);

  // Load nav groups + all nav modules (grouped client-side) for the Navigation step/tab.
  // Callable directly too, so we can refresh the preview right after saving a placement.
  const loadNavData = async () => {
    try {
      const [groupsRes, modulesRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/search/nav_groups/?limit=1000&sort_by=sip_order&sort_order=asc`, auth),
        axios.get(`${apiBaseUrl}/search/nav_modules/?limit=1000&sort_by=sip_order&sort_order=asc`, auth),
      ]);
      setNavGroups(groupsRes.data?.records || []);

      const mods = modulesRes.data?.records || [];
      const byGroup = {};
      mods.forEach((m) => {
        const gid = idOf(m.sip_navigation_group);
        if (!byGroup[gid]) byGroup[gid] = [];
        byGroup[gid].push(m);
      });
      Object.values(byGroup).forEach((list) => list.sort((a, b) => (a.sip_order || 0) - (b.sip_order || 0)));
      setNavModulesByGroup(byGroup);
      return byGroup;
    } catch (e) {
      console.error("Failed to load navigation groups/modules:", e);
      return {};
    }
  };

  useEffect(() => {
    loadNavData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, auth]);

  // Detect whether this table is already placed somewhere in the nav (edit mode only), once.
  useEffect(() => {
    if (isNew || !table.id || navPlacementLoaded || !Object.keys(navModulesByGroup).length) return;
    for (const [gid, mods] of Object.entries(navModulesByGroup)) {
      const found = mods.find((m) => idOf(m.sip_target_table) === table.id);
      if (found) {
        setSelectedGroupId(gid);
        setNavLabel(found.sip_label || table.label || "");
        setExistingNavModuleId(found.id);
        setNavPosition(mods.findIndex((m) => m.id === found.id));
        break;
      }
    }
    setNavPlacementLoaded(true);
  }, [isNew, table.id, table.label, navModulesByGroup, navPlacementLoaded]);

  // Load existing table + fields (+ choices for ENUM) when editing
  useEffect(() => {
    if (isNew) return;
    (async () => {
      const [tRes, fRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/tables/${recordId}`, auth),
        axios.get(`${apiBaseUrl}/search/table_fields/?table_id=${recordId}`, auth),
      ]);

      setTable(tRes.data);

      const fetched = (fRes.data.records || []).map((f) => ({
        ...f,
        choices: Array.isArray(f.choices) ? f.choices : [],
        field_type: f.field_type,
        varcharLength: isVarchar(f.field_type) ? parseVarcharLen(f.field_type) : 255,
      }));

      const enumFields = fetched.filter((f) => isEnum(f.field_type) && f.id);
      if (enumFields.length) {
        const pairs = await Promise.all(
          enumFields.map(async (f) => {
            const res = await axios.get(
              `${apiBaseUrl}/table_field_choices/?table_field_id=${f.id}`,
              auth
            );
            return { id: f.id, choices: res.data || [] };
          })
        );
        for (const { id, choices } of pairs) {
          const idx = fetched.findIndex((x) => x.id === id);
          if (idx !== -1) fetched[idx].choices = choices;
        }
      }

      setFields(fetched);
      setOriginalFields(JSON.parse(JSON.stringify(fetched)));
    })().catch((e) => console.error("Failed to load table form data:", e));
  }, [isNew, recordId, apiBaseUrl, auth]);

  // Load existing access rules for this table (edit mode only)
  useEffect(() => {
    if (isNew || !table.name) return;
    (async () => {
      try {
        const filter = { logic: "AND", conditions: [{ field: "table_name", operator: "is", value: table.name }] };
        const res = await axios.get(
          `${apiBaseUrl}/search/access_control_rules/?filter_json=${encodeURIComponent(JSON.stringify(filter))}&limit=1000`,
          auth
        );
        const rows = (res.data?.records || []).map((r) => ({
          id: r.id,
          role_id: idOf(r.role_id),
          allowed_methods: Array.isArray(r.allowed_methods) ? r.allowed_methods : [],
          script_condition: r.script_condition || "",
        }));
        setAclRules(rows);
      } catch (e) {
        console.error("Failed to load access rules:", e);
      }
    })();
  }, [isNew, table.name, apiBaseUrl, auth]);

  const handleTableChange = (e) => {
    setTable({ ...table, [e.target.name]: e.target.value });
  };

  const toggleAdvanced = (idx) =>
    setExpandedAdvanced((prev) => ({ ...prev, [idx]: !prev[idx] }));

  const hasDuplicateNames = (arr) => {
    const seen = new Set();
    for (const f of arr) {
      const n = (f.name || "").trim();
      if (!n) continue;
      if (seen.has(n)) return true;
      seen.add(n);
    }
    return false;
  };

  const handleLabelChange = (index, value) => {
    setFields((prev) => {
      const updated = [...prev];
      const f = { ...updated[index] };
      f.label = value;
      if (!f.id && !f.name) {
        f.name = slugify(value);
      }
      updated[index] = f;
      return updated;
    });
  };

  const handleFieldChange = (index, key, value) => {
    setFields((prev) => {
      const updated = [...prev];
      let f = { ...updated[index] };

      if (key === "name") {
        value = value.toLowerCase().replace(/\s+/g, "");
      }

      if (key === "field_type") {
        f.field_type = value;
        if (isEnum(value) && !Array.isArray(f.choices)) {
          f.choices = [{ id: null, value: "option_1", label: "Option 1", sip_order: 0, is_active: true }];
        }
        if (!isUUID(value)) {
          f.reference_table = "";
        }
        if (isVarchar(value)) {
          f.varcharLength = parseVarcharLen(value);
        } else if (value.toUpperCase() === "VARCHAR(255)") {
          f.varcharLength = 255;
        }
        f.default_value = coerceDefaultByType(value, f.default_value ?? "");
      } else if (key === "varcharLength") {
        const n = Number(value) || 1;
        f.varcharLength = n;
        f.field_type = `VARCHAR(${n})`;
      } else if (key === "default_value") {
        f.default_value = coerceDefaultByType(f.field_type, value);
      } else {
        f[key] = value;
      }

      updated[index] = f;
      return updated;
    });
  };

  const addFieldRow = () => {
    setFields((prev) => [
      ...prev,
      {
        id: null,
        name: "",
        label: "",
        field_type: "VARCHAR(255)",
        varcharLength: 255,
        reference_table: "",
        filter_table: "",
        reference_qualifier_script: "",
        default_value: "",
        is_mandatory: false,
        is_read_only: false,
        display: false,
        choices: [],
        created_at: null,
        created_by: null,
        updated_at: null,
        updated_by: null,
      },
    ]);
  };

  const duplicateField = (idx) => {
    setFields((prev) => {
      const copy = JSON.parse(JSON.stringify(prev[idx] || {}));
      copy.id = null;
      copy.name = "";
      copy.label = copy.label ? `${copy.label} (copy)` : "";
      if (Array.isArray(copy.choices)) {
        copy.choices = copy.choices.map((c) => ({ ...c, id: null }));
      }
      return [...prev, copy];
    });
  };

  const markFieldForDeletion = (fieldId, index) => {
    if (!fieldId) {
      setFields((prev) => prev.filter((_, i) => i !== index));
    } else {
      setDeletedFieldIds((prev) => [...prev, fieldId]);
      setFields((prev) => prev.filter((f) => f.id !== fieldId));
    }
  };

  const normalizeChoiceForCompare = (ch) => ({
    value: ch.value ?? "",
    label: ch.label ?? "",
    sip_order: Number.isFinite(ch.sip_order) ? ch.sip_order : 0,
    is_active: !!ch.is_active,
  });

  const isFieldChanged = (original, current) => {
    if (!original) return true;
    const baseChanged =
      original.label !== current.label ||
      original.field_type !== current.field_type ||
      (original.reference_table || "") !== (current.reference_table || "") ||
      (original.default_value || "") !== (current.default_value || "") ||
      !!original.is_mandatory !== !!current.is_mandatory ||
      !!original.is_read_only !== !!current.is_read_only ||
      !!original.display !== !!current.display ||
      (original.filter_table || "") !== (current.filter_table || "") ||
      (original.reference_qualifier_script || "") !== (current.reference_qualifier_script || "");

    if (baseChanged) return true;

    if (isEnum(current.field_type)) {
      const o = (original.choices || []).map(normalizeChoiceForCompare);
      const c = (current.choices || []).map(normalizeChoiceForCompare);
      return JSON.stringify(o) !== JSON.stringify(c);
    }
    return false;
  };

  // ----- Choice editor actions -----
  const addChoice = (fieldIdx) => {
    setFields((prev) => {
      const next = [...prev];
      const list = Array.isArray(next[fieldIdx].choices) ? next[fieldIdx].choices : [];
      const sip_order = list.length ? Math.max(...list.map((c) => c.sip_order || 0)) + 1 : 0;
      list.push({
        id: null,
        value: `option_${sip_order + 1}`,
        label: `Option ${sip_order + 1}`,
        sip_order,
        is_active: true,
      });
      next[fieldIdx].choices = [...list];
      return next;
    });
  };

  const updateChoice = (fieldIdx, choiceIdx, key, value) => {
    setFields((prev) => {
      const next = [...prev];
      const list = [...(next[fieldIdx].choices || [])];
      list[choiceIdx] = { ...list[choiceIdx], [key]: value };
      next[fieldIdx].choices = list;
      return next;
    });
  };

  const removeChoice = (fieldIdx, choiceIdx) => {
    setFields((prev) => {
      const next = [...prev];
      const list = [...(next[fieldIdx].choices || [])];
      list.splice(choiceIdx, 1);
      next[fieldIdx].choices = list.map((c, i) => ({ ...c, sip_order: i }));
      return next;
    });
  };

  const moveChoice = (fieldIdx, choiceIdx, dir) => {
    setFields((prev) => {
      const next = [...prev];
      const list = [...(next[fieldIdx].choices || [])];
      const newIdx = choiceIdx + dir;
      if (newIdx < 0 || newIdx >= list.length) return prev;
      const [row] = list.splice(choiceIdx, 1);
      list.splice(newIdx, 0, row);
      next[fieldIdx].choices = list.map((c, i) => ({ ...c, sip_order: i }));
      return next;
    });
  };

  const openCsvImport = (idx) => {
    setCsvOpenIdx(idx);
    setCsvDraft((p) => (p[idx] !== undefined ? p : { ...p, [idx]: "" }));
  };

  const closeCsvImport = () => setCsvOpenIdx(null);

  const applyCsvImport = (fieldIdx) => {
    const raw = csvDraft[fieldIdx] || "";
    const lines = raw.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!lines.length) {
      closeCsvImport();
      return;
    }
    const parsed = lines.map((line, i) => {
      const parts = line.split(",").map((p) => p.trim());
      let value, label;
      if (parts.length >= 2) {
        [value, label] = parts;
      } else {
        label = parts[0];
        value = slugify(label);
      }
      return { id: null, value, label, sip_order: i, is_active: true };
    });

    setFields((prev) => {
      const next = [...prev];
      next[fieldIdx].choices = parsed;
      return next;
    });
    toast.success(`Imported ${parsed.length} choice${parsed.length === 1 ? "" : "s"}.`);
    closeCsvImport();
  };

  // ----- Access control rule actions -----
  const aclKey = (r) => r.id || r._local;

  const addAclRule = () => {
    setAclRules((prev) => [
      ...prev,
      { id: null, role_id: "", allowed_methods: [], script_condition: "", _local: `local-${Date.now()}-${Math.random()}` },
    ]);
  };

  const patchAclRule = (key, patch) => {
    setAclRules((prev) => prev.map((r) => (aclKey(r) === key ? { ...r, ...patch } : r)));
  };

  const toggleAclMethod = (key, method) => {
    setAclRules((prev) =>
      prev.map((r) => {
        if (aclKey(r) !== key) return r;
        const has = r.allowed_methods.includes(method);
        return {
          ...r,
          allowed_methods: has ? r.allowed_methods.filter((m) => m !== method) : [...r.allowed_methods, method],
        };
      })
    );
  };

  const removeAclRule = async (rule) => {
    if (rule.id) {
      try {
        await axios.delete(`${apiBaseUrl}/access_control_rules/${rule.id}`, auth);
        toast.success("Access rule removed.");
      } catch (e) {
        console.error("Failed to remove access rule:", e);
        toast.error("Failed to remove access rule.");
        return;
      }
    }
    setAclRules((prev) => prev.filter((r) => aclKey(r) !== aclKey(rule)));
  };

  const saveAclRuleNow = async (rule) => {
    if (!rule.role_id) {
      toast.error("Select a role first.");
      return;
    }
    if (!rule.allowed_methods.length) {
      toast.error("Select at least one permission.");
      return;
    }

    const payload = {
      role_id: rule.role_id,
      table_name: table.name.toLowerCase(),
      allowed_methods: rule.allowed_methods,
      script_condition: rule.script_condition?.trim() || null,
    };

    try {
      setSavingRuleKey(aclKey(rule));
      if (rule.id) {
        await axios.put(`${apiBaseUrl}/access_control_rules/${rule.id}`, payload, auth);
        toast.success("Access rule updated.");
      } else {
        const res = await axios.post(`${apiBaseUrl}/access_control_rules/`, payload, auth);
        const newId = res.data?.record?.id || res.data?.id;
        setAclRules((prev) => prev.map((r) => (aclKey(r) === aclKey(rule) ? { ...r, id: newId } : r)));
        toast.success("Access rule created.");
      }
    } catch (e) {
      console.error("Failed to save access rule:", e);
      toast.error("Failed to save access rule.");
    } finally {
      setSavingRuleKey(null);
    }
  };

  const validAclRules = () => aclRules.filter((r) => r.role_id && r.allowed_methods.length > 0);
  const incompleteAclRules = () =>
    aclRules.filter((r) => (r.role_id && !r.allowed_methods.length) || (!r.role_id && r.allowed_methods.length > 0));

  // ----- Navigation placement -----
  // Everything already sitting in the target group, minus this table's own row (so it isn't
  // shown twice — once as a normal item and once as the "this table" placeholder).
  const groupModules = (navModulesByGroup[selectedGroupId] || []).filter((m) => m.id !== existingNavModuleId);

  const handleGroupChange = (newGroupId) => {
    setNewGroupMode(false);
    setSelectedGroupId(newGroupId);
    const list = (navModulesByGroup[newGroupId] || []).filter((m) => m.id !== existingNavModuleId);
    setNavPosition(newGroupId === selectedGroupId ? navPosition : list.length);
  };

  const moveNavPosition = (dir) => {
    setNavPosition((p) => Math.max(0, Math.min(groupModules.length, p + dir)));
  };

  // Sequentially renumber the sip_order of every module around the insertion point, only
  // sending a PUT for rows whose order actually changes, and return the order to use for
  // this table's own module.
  const persistNavOrdering = async (list, insertIndex) => {
    const before = list.slice(0, insertIndex);
    const after = list.slice(insertIndex);
    let order = 1;

    for (const m of before) {
      if (m.sip_order !== order) {
        await axios.put(`${apiBaseUrl}/nav_modules/${m.id}`, { sip_order: order }, auth).catch((e) => {
          console.error("Failed to reorder nav module:", e);
        });
      }
      order += 1;
    }

    const myOrder = order;
    order += 1;

    for (const m of after) {
      if (m.sip_order !== order) {
        await axios.put(`${apiBaseUrl}/nav_modules/${m.id}`, { sip_order: order }, auth).catch((e) => {
          console.error("Failed to reorder nav module:", e);
        });
      }
      order += 1;
    }

    return myOrder;
  };

  // Used by the wizard (new table): stages nothing itself, called after the table + fields
  // have actually been created, since sip_target_table needs a real table id.
  const commitNavPlacement = async (newTableId) => {
    if (!selectedGroupId && !newGroupMode) return;

    let groupId = selectedGroupId;
    if (newGroupMode) {
      const gRes = await axios.post(
        `${apiBaseUrl}/nav_groups/`,
        {
          sip_name: slugify(newGroupLabel || "group"),
          sip_label: newGroupLabel || "New Group",
          sip_order: Math.max(0, ...navGroups.map((g) => g.sip_order || 0)) + 1,
        },
        auth
      );
      groupId = gRes.data?.record?.id || gRes.data?.id;
    }
    if (!groupId) return;

    const list = (navModulesByGroup[groupId] || []).filter((m) => m.id !== existingNavModuleId);
    const finalOrder = await persistNavOrdering(list, navPosition);

    await axios.post(
      `${apiBaseUrl}/nav_modules/`,
      {
        sip_navigation_group: groupId,
        sip_label: navLabel || table.label,
        sip_target_table: newTableId,
        sip_order: finalOrder,
      },
      auth
    );
  };

  // Used by the edit-mode Navigation tab: fires immediately, same shape as the ACL rows.
  const saveNavigationNow = async () => {
    try {
      setSavingNav(true);

      if (!selectedGroupId && !newGroupMode) {
        if (existingNavModuleId) {
          await axios.delete(`${apiBaseUrl}/nav_modules/${existingNavModuleId}`, auth);
          setExistingNavModuleId(null);
          toast.success("Removed from navigation.");
          await loadNavData();
        }
        return;
      }

      let groupId = selectedGroupId;
      if (newGroupMode) {
        const gRes = await axios.post(
          `${apiBaseUrl}/nav_groups/`,
          {
            sip_name: slugify(newGroupLabel || "group"),
            sip_label: newGroupLabel || "New Group",
            sip_order: Math.max(0, ...navGroups.map((g) => g.sip_order || 0)) + 1,
          },
          auth
        );
        groupId = gRes.data?.record?.id || gRes.data?.id;
        setNewGroupMode(false);
        setSelectedGroupId(groupId);
      }

      const list = (navModulesByGroup[groupId] || []).filter((m) => m.id !== existingNavModuleId);
      const finalOrder = await persistNavOrdering(list, navPosition);

      if (existingNavModuleId) {
        await axios.put(
          `${apiBaseUrl}/nav_modules/${existingNavModuleId}`,
          { sip_navigation_group: groupId, sip_label: navLabel || table.label, sip_order: finalOrder },
          auth
        );
      } else {
        const res = await axios.post(
          `${apiBaseUrl}/nav_modules/`,
          {
            sip_navigation_group: groupId,
            sip_label: navLabel || table.label,
            sip_target_table: table.id,
            sip_order: finalOrder,
          },
          auth
        );
        setExistingNavModuleId(res.data?.record?.id || res.data?.id);
      }

      toast.success("Navigation placement saved.");
      await loadNavData();
    } catch (e) {
      console.error("Failed to save navigation placement:", e);
      toast.error("Failed to save navigation placement.");
    } finally {
      setSavingNav(false);
    }
  };

  // ----- validation -----
  const validate = () => {
    const errs = [];
    if (!table.name || !table.label) errs.push("Name and Label are required for the table.");
    if (fields.some((f) => !f.name || !f.label || !f.field_type)) {
      errs.push("All fields must have a Name, Label, and Type.");
    }
    if (hasDuplicateNames(fields)) {
      errs.push("Duplicate field names detected. Each field 'name' must be unique.");
    }

    for (const f of fields) {
      // Only enforce Reference Table for UUID *reference* fields, not the PK.
      if (isUUID(f.field_type) && f.name !== "id" && !f.reference_table) {
        errs.push(`Field "${f.label || f.name}" is UUID but has no Reference Table selected.`);
      }
    }

    if (isNew && incompleteAclRules().length > 0) {
      errs.push("Some access rules are missing a role or permissions — finish or remove them.");
    }

    if (isNew && newGroupMode && !newGroupLabel.trim()) {
      errs.push("Give the new navigation group a name, or switch back to an existing group.");
    }

    setErrors(errs);
    return errs.length === 0;
  };

  // ----- Save -----
  const saveTable = async () => {
    if (!validate()) {
      toast.error("Please fix the highlighted issues first.");
      return;
    }

    try {
      setIsSaving(true);

      if (isNew) {
        const payloadFields = fields
          .filter((f) => !f.id)
          .map((f) => {
            const base = {
              label: f.label,
              field_type: f.field_type,
              reference_table: f.reference_table || null,
              is_mandatory: !!f.is_mandatory,
              is_read_only: !!f.is_read_only,
              display: !!f.display,
              filter_table: f.filter_table || null,
              reference_qualifier_script: f.reference_qualifier_script || null,
              default_value: f.default_value ?? "",
              length: isVarchar(f.field_type) ? f.varcharLength : null,
              name:
                table.name === "table_field_choices"
                  ? f.name
                  : f.name.startsWith("sip_")
                  ? f.name
                  : `sip_${f.name}`,
            };
            if (isEnum(f.field_type)) {
              base.choices = (f.choices || []).map((c) => ({
                value: c.value ?? "",
                label: c.label ?? "",
                sip_order: Number.isFinite(c.sip_order) ? c.sip_order : 0,
                is_active: !!c.is_active,
              }));
            }
            return base;
          });

        const res = await axios.post(
          `${apiBaseUrl}/tables/`,
          { name: table.name, label: table.label, fields: payloadFields },
          auth
        );

        const rulesToCreate = validAclRules();
        let failedRules = 0;
        for (const rule of rulesToCreate) {
          try {
            await axios.post(
              `${apiBaseUrl}/access_control_rules/`,
              {
                role_id: rule.role_id,
                table_name: table.name.toLowerCase(),
                allowed_methods: rule.allowed_methods,
                script_condition: rule.script_condition?.trim() || null,
              },
              auth
            );
          } catch (e) {
            console.error("Failed to create access rule:", e);
            failedRules += 1;
          }
        }

        if (failedRules > 0) {
          toast.error(`Table created, but ${failedRules} access rule(s) failed to save. Add them from the Access Control tab.`);
        } else {
          toast.success("Table created successfully.");
        }

        const newId = res.data?.table?.id || res.data?.record?.id || res.data?.id;

        if (newId && (selectedGroupId || newGroupMode)) {
          try {
            await commitNavPlacement(newId);
          } catch (e) {
            console.error("Failed to place table in navigation:", e);
            toast.error("Table created, but couldn't add it to the navigation menu. You can do this from Edit → Navigation.");
          }
        }

        if (newId) {
          window.location.href = `/dashboard/tables/form_view/${newId}`;
        } else {
          window.location.reload();
        }
        return;
      }

      await axios.put(`${apiBaseUrl}/tables/update?table_name=${table.name}`, { label: table.label }, auth);

      for (const id of deletedFieldIds) {
        await axios.delete(`${apiBaseUrl}/table_fields/${id}`, auth);
      }

      for (const f of fields) {
        const base = {
          label: f.label,
          field_type: f.field_type,
          reference_table: f.reference_table || null,
          is_mandatory: !!f.is_mandatory,
          is_read_only: !!f.is_read_only,
          display: !!f.display,
          filter_table: f.filter_table || null,
          reference_qualifier_script: f.reference_qualifier_script || null,
          default_value: f.default_value ?? "",
          length: isVarchar(f.field_type) ? f.varcharLength : null,
        };
        const original = originalFields.find((of) => of.id === f.id);

        if (f.id) {
          if (isFieldChanged(original, f)) {
            const updatePayload = { ...base };
            if (isEnum(f.field_type)) {
              updatePayload.choices = (f.choices || []).map((c) => ({
                value: c.value ?? "",
                label: c.label ?? "",
                sip_order: Number.isFinite(c.sip_order) ? c.sip_order : 0,
                is_active: !!c.is_active,
              }));
            }
            await axios.put(`${apiBaseUrl}/table_fields/${f.id}`, updatePayload, auth);
          }
        } else {
          const createPayload = {
            ...base,
            name: f.name.startsWith("sip_") ? f.name : `sip_${f.name}`,
            table_id: table.id,
          };
          if (isEnum(f.field_type)) {
            createPayload.choices = (f.choices || []).map((c) => ({
              value: c.value ?? "",
              label: c.label ?? "",
              sip_order: Number.isFinite(c.sip_order) ? c.sip_order : 0,
              is_active: !!c.is_active,
            }));
          }
          await axios.post(`${apiBaseUrl}/table_fields/`, createPayload, auth);
        }
      }

      toast.success("Table saved successfully.");
      window.location.reload();
    } catch (error) {
      console.error("Error saving table:", error);
      toast.error("Failed to save the table. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteTable = async () => {
    if (!window.confirm("Are you sure you want to delete this table? This cannot be undone.")) return;
    try {
      await axios.delete(`${apiBaseUrl}/tables/${recordId}`, auth);
      toast.success("Table deleted successfully.");
      navigate("/dashboard");
    } catch (err) {
      console.error("Failed to delete table:", err);
      toast.error("Could not delete table. Please try again.");
    }
  };

  const canLeaveBasics = () => {
    if (!table.name || !table.label) {
      toast.error("Give the table a name and a label first.");
      return false;
    }
    return true;
  };

  const DefaultValueEditor = ({ f, index }) => {
    const t = (f.field_type || "").toUpperCase();

    if (t === "BOOLEAN") {
      return (
        <label style={styles.checkboxLabel} title="Default value used on create">
          <input
            type="checkbox"
            checked={!!f.default_value}
            onChange={(e) => handleFieldChange(index, "default_value", e.target.checked)}
            style={styles.checkbox}
          />
          Default: {f.default_value ? "True" : "False"}
        </label>
      );
    }

    if (t === "INTEGER") {
      return (
        <input
          type="number"
          value={f.default_value ?? ""}
          onChange={(e) => handleFieldChange(index, "default_value", e.target.value)}
          style={styles.inputMd}
          placeholder="Default (integer)"
          title="Default value used on create"
        />
      );
    }

    if (t === "NUMERIC") {
      return (
        <input
          type="number"
          step="any"
          value={f.default_value ?? ""}
          onChange={(e) => handleFieldChange(index, "default_value", e.target.value)}
          style={styles.inputMd}
          placeholder="Default (decimal)"
          title="Default value used on create"
        />
      );
    }

    if (t === "DATE") {
      return (
        <input
          type="date"
          value={f.default_value ?? ""}
          onChange={(e) => handleFieldChange(index, "default_value", e.target.value)}
          style={styles.inputMd}
          placeholder="Default (YYYY-MM-DD)"
          title="Default value used on create"
        />
      );
    }

    if (t === "TIMESTAMP") {
      return (
        <input
          type="datetime-local"
          value={f.default_value ?? ""}
          onChange={(e) => handleFieldChange(index, "default_value", e.target.value)}
          style={styles.inputMd}
          placeholder="Default (ISO)"
          title="Default value used on create"
        />
      );
    }

    if (t === "ENUM") {
      const active = (f.choices || []).filter((c) => !!c.is_active);
      return (
        <select
          value={f.default_value || ""}
          onChange={(e) => handleFieldChange(index, "default_value", e.target.value)}
          style={styles.inputMd}
          title="Default option on create"
        >
          <option value="">— No default —</option>
          {active.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label || c.value}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        value={f.default_value ?? ""}
        onChange={(e) => handleFieldChange(index, "default_value", e.target.value)}
        style={styles.inputMd}
        placeholder={(f.field_type || "").toUpperCase() === "UUID" ? "Default UUID (optional)" : "Default value (optional)"}
        title="Default value used on create"
      />
    );
  };

  const MethodToggle = ({ methods, onToggle }) => (
    <div style={styles.methodRow}>
      {METHOD_DEFS.map(({ key, label, icon: Icon }) => {
        const active = methods.includes(key);
        return (
          <button
            key={key}
            type="button"
            onClick={() => onToggle(key)}
            style={{ ...styles.methodChip, ...(active ? styles.methodChipActive : {}) }}
            title={`${label} (${key})`}
          >
            <Icon size={12} /> {label}
          </button>
        );
      })}
    </div>
  );

  const AclRuleCard = ({ rule }) => {
    const key = aclKey(rule);
    const isSavingThis = savingRuleKey === key;
    const isUnsaved = !rule.id;
    const roleName = roles.find((r) => r.id === rule.role_id)?.name;

    return (
      <div style={styles.aclCard}>
        <div style={styles.aclCardTop}>
          <div style={styles.aclRoleWrap}>
            <Shield size={14} color="var(--brand, #0d7d6f)" />
            <select
              value={rule.role_id || ""}
              onChange={(e) => patchAclRule(key, { role_id: e.target.value })}
              style={styles.inputMd}
              title="Role this rule applies to"
            >
              <option value="">— Select role —</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            {!isNew && isUnsaved && <span style={styles.unsavedPill}>Unsaved</span>}
          </div>

          <div style={{ display: "flex", gap: 6 }}>
            {!isNew && (
              <button
                type="button"
                onClick={() => saveAclRuleNow(rule)}
                disabled={isSavingThis}
                style={styles.iconBtnPrimary}
                title={rule.id ? "Update this rule" : "Create this rule"}
              >
                {isSavingThis ? (
                  <Loader2 size={14} style={{ animation: "lv-spin 0.8s linear infinite" }} />
                ) : (
                  <Save size={14} />
                )}
              </button>
            )}
            <button type="button" onClick={() => removeAclRule(rule)} style={styles.iconBtnDanger} title="Remove rule">
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        <MethodToggle methods={rule.allowed_methods} onToggle={(m) => toggleAclMethod(key, m)} />

        <div style={{ marginTop: 10 }}>
          <div style={styles.helpLabel}>Row-level condition (optional)</div>
          <input
            value={rule.script_condition || ""}
            onChange={(e) => patchAclRule(key, { script_condition: e.target.value })}
            placeholder="e.g. department_id.department_head_id = CURRENT_USER_ID"
            style={styles.inputMd}
            title="Leave blank to grant access to every row this role can otherwise see"
          />
        </div>

        {roleName && (
          <div style={styles.aclSummaryLine}>
            <strong>{roleName}</strong> can{" "}
            {rule.allowed_methods.length
              ? rule.allowed_methods.map((m) => METHOD_DEFS.find((d) => d.key === m)?.label).join(", ").toLowerCase()
              : "— nothing yet —"}
            {" "}on this table{rule.script_condition ? ", when the condition matches" : ""}.
          </div>
        )}
      </div>
    );
  };

  const renderFieldsPanel = () => (
    <>
      <div style={styles.sectionHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Layers size={16} color="var(--brand, #0d7d6f)" />
          <h3 style={{ margin: 0 }}>Fields</h3>
          <span style={styles.countPill}>{fields.length}</span>
        </div>
        <button onClick={addFieldRow} style={styles.btnSuccess} type="button">
          <Plus size={14} /> Add Field
        </button>
      </div>

      {fields.length === 0 && (
        <div style={styles.emptyState}>
          <Sparkles size={20} color="var(--muted, #5c6d6f)" />
          <div>No fields yet. Add the columns this table needs.</div>
        </div>
      )}

      {fields.map((f, index) => {
        const FieldIcon = fieldTypeIcon(f.field_type);
        const isPrimaryKey = f.name === "id";
        return (
          <div key={f.id || index} style={styles.fieldCard}>
            {(!!f.created_at || !!f.updated_at) && (
              <div style={styles.auditRow}>
                {f.created_at ? (
                  <span style={styles.badge} title="Created at/by">
                    {new Date(f.created_at).toLocaleString()} {f.created_by?.display_value ? `by ${f.created_by.display_value}` : ""}
                  </span>
                ) : null}
                {f.updated_at ? (
                  <span style={styles.badge} title="Updated at/by">
                    Updated {new Date(f.updated_at).toLocaleString()} {f.updated_by?.display_value ? `by ${f.updated_by.display_value}` : ""}
                  </span>
                ) : null}
              </div>
            )}

            <div style={styles.fieldMainRow}>
              <div>
                <div style={styles.helpLabel}>Name</div>
                <div style={styles.fieldNameWrap}>
                  <span style={styles.fieldTypeIconWrap}>
                    {isPrimaryKey ? <KeyRound size={13} /> : <FieldIcon size={13} />}
                  </span>
                  <input
                    placeholder="name"
                    value={f.name}
                    disabled={!!f.id}
                    onChange={(e) => handleFieldChange(index, "name", e.target.value)}
                    style={styles.inputMd}
                    title="DB column name (auto 'sip_' prefix on save)"
                  />
                </div>
              </div>

              <div>
                <div style={styles.helpLabel}>Label</div>
                <input
                  placeholder="Label"
                  value={f.label}
                  onChange={(e) => handleLabelChange(index, e.target.value)}
                  style={styles.inputMd}
                  title="Shown in the UI"
                />
              </div>

              <div>
                <div style={styles.helpLabel}>Type</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <select
                    value={f.field_type}
                    onChange={(e) => handleFieldChange(index, "field_type", e.target.value)}
                    style={styles.inputMd}
                    title="Field data type"
                  >
                    {FIELD_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {isVarchar(f.field_type) && (
                    <input
                      type="number"
                      min={1}
                      max={10485760}
                      value={f.varcharLength ?? 255}
                      onChange={(e) => handleFieldChange(index, "varcharLength", e.target.value)}
                      style={{ ...styles.inputSm, width: 110 }}
                      title="VARCHAR length"
                    />
                  )}
                </div>
              </div>

              <div>
                <div style={styles.helpLabel}>Default</div>
                <DefaultValueEditor f={f} index={index} />
              </div>
            </div>

            <div style={styles.fieldSecondRow}>
              <div>
                <div style={styles.helpLabel}>Filter Table (optional)</div>
                <input
                  placeholder="Filter table"
                  value={f.filter_table || ""}
                  onChange={(e) => handleFieldChange(index, "filter_table", e.target.value)}
                  style={styles.inputMd}
                  title="Used by UI filters"
                />
              </div>

              <div>
                <div style={styles.helpLabel}>Reference Table</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <select
                    value={f.reference_table || ""}
                    onChange={(e) => handleFieldChange(index, "reference_table", e.target.value)}
                    style={styles.inputMd}
                    disabled={!isUUID(f.field_type) || f.name === "id"}
                    title={f.name === "id" ? "Primary key UUID — no reference" : "Only for UUID reference fields"}
                  >
                    <option value="">{f.name === "id" ? "— Not applicable for primary key —" : "— Select a table —"}</option>
                    {tablesOptions.map((t) => (
                      <option key={t.id} value={t.name}>
                        {t.label || t.name}
                      </option>
                    ))}
                    {f.reference_table && !tablesOptions.some((t) => t.name === f.reference_table) ? (
                      <option value={f.reference_table}>{f.reference_table} (unknown)</option>
                    ) : null}
                  </select>

                  {f.reference_table && f.default_value ? (
                    <span
                      title="Go to reference record"
                      onClick={() => navigate(`/${f.reference_table}/${f.default_value}`)}
                      style={styles.inlineLinkIcon}
                    >
                      <Link2 size={16} />
                    </span>
                  ) : null}
                </div>
              </div>

              {f.reference_table ? (
                <div>
                  <div style={styles.helpLabel}>Reference Qualifier Script (optional)</div>
                  <textarea
                    placeholder={"filter_json = {\"conditions\": [{\"field\": \"...\", \"operator\": \"is\", \"value\": current_record.get(\"...\")}]}"}
                    value={f.reference_qualifier_script || ""}
                    onChange={(e) => handleFieldChange(index, "reference_qualifier_script", e.target.value)}
                    style={{ ...styles.inputMd, width: "100%", minHeight: 60, fontFamily: "monospace", fontSize: 12 }}
                    title="Python -- set filter_json (same shape as Advanced Filter) or allowed_ids to scope this field's reference picker to the record being edited (current_record). For the full syntax-highlighted editor, edit this field's row directly under Table Fields."
                  />
                </div>
              ) : null}

              <div>
                <div style={styles.helpLabel}>Behavior</div>
                <div style={styles.checkboxGroup}>
                  <label style={styles.checkboxLabel} title="Require a value on create">
                    <input
                      type="checkbox"
                      checked={!!f.is_mandatory}
                      onChange={(e) => handleFieldChange(index, "is_mandatory", e.target.checked)}
                      style={styles.checkbox}
                    />
                    Mandatory
                  </label>

                  <label style={styles.checkboxLabel} title="Show in default list/detail views">
                    <input
                      type="checkbox"
                      checked={!!f.display}
                      onChange={(e) => handleFieldChange(index, "display", e.target.checked)}
                      style={styles.checkbox}
                    />
                    Display
                  </label>

                  <label style={styles.checkboxLabel} title="Prevent editing in UI">
                    <input
                      type="checkbox"
                      checked={!!f.is_read_only}
                      onChange={(e) => handleFieldChange(index, "is_read_only", e.target.checked)}
                      style={styles.checkbox}
                    />
                    Read Only
                  </label>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "flex-end" }}>
                <button onClick={() => duplicateField(index)} style={styles.btn} type="button" title="Duplicate field">
                  <Copy size={13} /> Duplicate
                </button>
                <button onClick={() => toggleAdvanced(index)} style={styles.btn} type="button">
                  {expandedAdvanced[index] ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Advanced
                </button>
                <button onClick={() => markFieldForDeletion(f.id, index)} style={styles.btnDanger} type="button">
                  <Trash2 size={13} /> Remove
                </button>
              </div>
            </div>

            {expandedAdvanced[index] ? (
              <div style={styles.advancedPanel}>
                <div style={styles.advancedGrid}>
                  <div>
                    <div style={styles.advLabel}>Internal Name (saved)</div>
                    <code>
                      {table.name === "table_field_choices"
                        ? f.name
                        : f.name?.startsWith("sip_")
                        ? f.name
                        : `sip_${f.name || ""}`}
                    </code>
                  </div>
                  <div>
                    <div style={styles.advLabel}>Type String</div>
                    <code>{f.field_type}</code>
                  </div>
                  {isVarchar(f.field_type) ? (
                    <div>
                      <div style={styles.advLabel}>Length (DDL)</div>
                      <code>{f.varcharLength}</code>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {isEnum(f.field_type) ? (
              <div style={styles.choiceWrap}>
                <div style={styles.choiceHeader}>
                  <strong>Choices</strong>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button style={styles.btn} onClick={() => addChoice(index)} type="button">
                      <Plus size={13} /> Add Choice
                    </button>
                    <button style={styles.btn} onClick={() => openCsvImport(index)} type="button">
                      <Download size={13} /> Import List
                    </button>
                  </div>
                </div>

                {csvOpenIdx === index && (
                  <div style={styles.csvPanel}>
                    <div style={styles.helpLabel}>
                      One choice per line — either <code>value,label</code> or just <code>label</code> (value gets slugified).
                    </div>
                    <textarea
                      value={csvDraft[index] || ""}
                      onChange={(e) => setCsvDraft((p) => ({ ...p, [index]: e.target.value }))}
                      placeholder={"active,Active\ninactive,Inactive"}
                      style={styles.csvTextarea}
                    />
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <button style={styles.btnGhostSm} onClick={closeCsvImport} type="button">
                        Cancel
                      </button>
                      <button style={styles.btnPrimarySm} onClick={() => applyCsvImport(index)} type="button">
                        <Check size={13} /> Import
                      </button>
                    </div>
                  </div>
                )}

                {(f.choices || []).length > 0 ? (
                  <>
                    <div style={styles.choiceGridHeader}>
                      <div>Order</div>
                      <div>Value</div>
                      <div>Label</div>
                      <div>Active</div>
                      <div>Actions</div>
                    </div>

                    <div style={{ maxHeight: 260, overflowY: "auto", paddingRight: 2 }}>
                      {f.choices.map((ch, ci) => (
                        <div key={ch.id || `${ci}`} style={styles.choiceRow}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <button
                              style={styles.btnGhost}
                              onClick={() => moveChoice(index, ci, -1)}
                              disabled={ci === 0}
                              type="button"
                            >
                              <ArrowUp size={12} />
                            </button>
                            <button
                              style={styles.btnGhost}
                              onClick={() => moveChoice(index, ci, +1)}
                              disabled={ci === (f.choices?.length || 1) - 1}
                              type="button"
                            >
                              <ArrowDown size={12} />
                            </button>
                            <code style={{ opacity: 0.7 }}>{ch.sip_order ?? ci}</code>
                          </div>

                          <input
                            value={ch.value || ""}
                            onChange={(e) => updateChoice(index, ci, "value", e.target.value)}
                            placeholder="value (stored)"
                            style={styles.choiceInput}
                          />
                          <input
                            value={ch.label || ""}
                            onChange={(e) => updateChoice(index, ci, "label", e.target.value)}
                            placeholder="label (shown)"
                            style={styles.choiceInput}
                          />
                          <input
                            type="checkbox"
                            checked={!!ch.is_active}
                            onChange={(e) => updateChoice(index, ci, "is_active", e.target.checked)}
                          />
                          <button style={styles.btnDangerOutline} onClick={() => removeChoice(index, ci)} type="button">
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={styles.choiceEmpty}>No choices yet.</div>
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );

  const renderAccessPanel = () => (
    <>
      <div style={styles.sectionHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Shield size={16} color="var(--brand, #0d7d6f)" />
          <h3 style={{ margin: 0 }}>Access Control</h3>
          <span style={styles.countPill}>{aclRules.length}</span>
        </div>
        <button onClick={addAclRule} style={styles.btnSuccess} type="button">
          <Plus size={14} /> Add Role Access
        </button>
      </div>

      <div style={styles.infoBanner}>
        <Info size={15} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          Without an access rule, only <strong>super_admin</strong> can see or use this table — every other role is denied
          by default. Add a rule per role that should have access, and pick exactly which actions (view/create/edit/delete)
          they're allowed to perform.
        </div>
      </div>

      {aclRules.length === 0 && (
        <div style={styles.emptyState}>
          <Shield size={20} color="var(--muted, #5c6d6f)" />
          <div>No access rules yet. Click "Add Role Access" to grant a role permissions on this table.</div>
        </div>
      )}

      {aclRules.map((rule) => (
        <AclRuleCard key={aclKey(rule)} rule={rule} />
      ))}
    </>
  );

  const renderNavigationPanel = () => (
    <>
      <div style={styles.sectionHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Compass size={16} color="var(--brand, #0d7d6f)" />
          <h3 style={{ margin: 0 }}>Navigation</h3>
        </div>
        {!isNew && (
          <button onClick={saveNavigationNow} disabled={savingNav} style={styles.btnSuccess} type="button">
            {savingNav ? <Loader2 size={14} style={{ animation: "lv-spin 0.8s linear infinite" }} /> : <Save size={14} />}
            {savingNav ? "Saving..." : "Save Navigation"}
          </button>
        )}
      </div>

      <div style={styles.infoBanner}>
        <Info size={15} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          Placing this table in a navigation group is optional, but it's the only way people will find it in the sidebar
          without a direct link. Pick a group and exactly where in its list this table should sit.
        </div>
      </div>

      <div style={styles.navConfigGrid}>
        <div>
          <div style={styles.helpLabel}>Navigation Group</div>
          {newGroupMode ? (
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={newGroupLabel}
                onChange={(e) => setNewGroupLabel(e.target.value)}
                placeholder="e.g. People Operations"
                style={styles.inputMd}
              />
              <button type="button" style={styles.btnGhostSm} onClick={() => setNewGroupMode(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <select value={selectedGroupId} onChange={(e) => handleGroupChange(e.target.value)} style={styles.inputMd}>
                <option value="">— Not in navigation —</option>
                {navGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.sip_label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                style={styles.btnGhostSm}
                onClick={() => {
                  setNewGroupMode(true);
                  setSelectedGroupId("");
                  setNavPosition(0);
                }}
              >
                <FolderPlus size={13} /> New Group
              </button>
            </div>
          )}
        </div>

        {(selectedGroupId || newGroupMode) && (
          <div>
            <div style={styles.helpLabel}>Label in Navigation</div>
            <input
              value={navLabel}
              onChange={(e) => setNavLabel(e.target.value)}
              placeholder={table.label || "Same as table label"}
              style={styles.inputMd}
            />
          </div>
        )}
      </div>

      {selectedGroupId && !newGroupMode && (
        <div style={styles.navPreviewWrap}>
          <div style={styles.navPreviewHeader}>
            <MoveVertical size={13} /> Preview — exactly how this group will look
          </div>
          <div style={styles.navPreviewShell}>
            {groupModules.slice(0, navPosition).map((m) => {
              const Icon = moduleIcon(m.sip_label, "");
              return (
                <div key={m.id} style={styles.navPreviewItem}>
                  <span style={styles.navPreviewIcon}>
                    <Icon size={12} />
                  </span>
                  {m.sip_label}
                </div>
              );
            })}

            <div style={styles.navPreviewItemNew}>
              <span style={styles.navPreviewIconNew}>
                <Sparkles size={12} />
              </span>
              <span style={{ flex: 1 }}>{navLabel || table.label || "This table"}</span>
              <span style={styles.newBadge}>NEW</span>
              <div style={styles.navPreviewMoveBtns}>
                <button
                  type="button"
                  onClick={() => moveNavPosition(-1)}
                  disabled={navPosition === 0}
                  style={{ ...styles.navMoveBtn, opacity: navPosition === 0 ? 0.4 : 1 }}
                  title="Move up"
                >
                  <ChevronUp size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => moveNavPosition(1)}
                  disabled={navPosition >= groupModules.length}
                  style={{ ...styles.navMoveBtn, opacity: navPosition >= groupModules.length ? 0.4 : 1 }}
                  title="Move down"
                >
                  <ChevronDown size={12} />
                </button>
              </div>
            </div>

            {groupModules.slice(navPosition).map((m) => {
              const Icon = moduleIcon(m.sip_label, "");
              return (
                <div key={m.id} style={styles.navPreviewItem}>
                  <span style={styles.navPreviewIcon}>
                    <Icon size={12} />
                  </span>
                  {m.sip_label}
                </div>
              );
            })}

            {groupModules.length === 0 && <div style={styles.navPreviewEmpty}>This group is otherwise empty.</div>}
          </div>
        </div>
      )}

      {newGroupMode && (
        <div style={styles.navPreviewWrap}>
          <div style={styles.navPreviewHeader}>
            <MoveVertical size={13} /> Preview
          </div>
          <div style={styles.navPreviewShell}>
            <div style={styles.navPreviewItemNew}>
              <span style={styles.navPreviewIconNew}>
                <Sparkles size={12} />
              </span>
              <span style={{ flex: 1 }}>{navLabel || table.label || "This table"}</span>
              <span style={styles.newBadge}>NEW</span>
            </div>
            <div style={styles.navPreviewEmpty}>First item in this new group.</div>
          </div>
        </div>
      )}

      {!selectedGroupId && !newGroupMode && (
        <div style={styles.emptyState}>
          <Compass size={20} color="var(--muted, #5c6d6f)" />
          <div>This table won't appear in the sidebar until you place it in a navigation group.</div>
        </div>
      )}
    </>
  );

  const renderBasicsPanel = () => (
    <div style={styles.basicsCard}>
      <div style={styles.formGrid}>
        <div>
          <label style={styles.label}>Table Name</label>
          <input
            name="name"
            value={table.name}
            disabled={!isNew}
            onChange={handleTableChange}
            style={styles.inputLg}
            placeholder="table_name"
          />
          <div style={styles.fieldHint}>Lowercase, no spaces — this becomes the real database table name.</div>
        </div>

        <div>
          <label style={styles.label}>Label</label>
          <input
            name="label"
            value={table.label}
            onChange={handleTableChange}
            style={styles.inputLg}
            placeholder="Human readable name"
          />
          <div style={styles.fieldHint}>Shown throughout the UI — navigation, list views, form titles.</div>
        </div>
      </div>
    </div>
  );

  const renderReviewPanel = () => {
    const rules = validAclRules();
    return (
      <div style={styles.basicsCard}>
        <div style={styles.reviewSection}>
          <div style={styles.reviewSectionTitle}>
            <Table2 size={14} /> Table
          </div>
          <div style={styles.reviewLine}>
            <strong>{table.label || "—"}</strong> <code>{table.name || "—"}</code>
          </div>
        </div>

        <div style={styles.reviewSection}>
          <div style={styles.reviewSectionTitle}>
            <Layers size={14} /> Fields ({fields.length})
          </div>
          {fields.length === 0 ? (
            <div style={styles.fieldHint}>No fields added.</div>
          ) : (
            <div style={styles.reviewFieldGrid}>
              {fields.map((f, i) => {
                const FieldIcon = fieldTypeIcon(f.field_type);
                return (
                  <div key={f.id || i} style={styles.reviewFieldChip}>
                    <FieldIcon size={12} /> {f.label || f.name || "(unnamed)"}
                    {f.is_mandatory && <span style={styles.requiredDot} title="Mandatory" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={styles.reviewSection}>
          <div style={styles.reviewSectionTitle}>
            <Shield size={14} /> Access Rules ({rules.length})
          </div>
          {rules.length === 0 ? (
            <div style={styles.fieldHint}>
              No access rules staged — only super_admin will be able to use this table until you add some (you can still do
              that later from Edit → Access Control).
            </div>
          ) : (
            rules.map((r) => (
              <div key={aclKey(r)} style={styles.reviewLine}>
                <strong>{roles.find((x) => x.id === r.role_id)?.name || "Unknown role"}</strong>:{" "}
                {r.allowed_methods.map((m) => METHOD_DEFS.find((d) => d.key === m)?.label).join(", ")}
                {r.script_condition ? ` (condition: ${r.script_condition})` : ""}
              </div>
            ))
          )}
        </div>

        <div style={styles.reviewSection}>
          <div style={styles.reviewSectionTitle}>
            <Compass size={14} /> Navigation
          </div>
          {newGroupMode ? (
            <div style={styles.reviewLine}>
              Will appear in a new group, <strong>{newGroupLabel || "(unnamed)"}</strong>, as "{navLabel || table.label}".
            </div>
          ) : selectedGroupId ? (
            <div style={styles.reviewLine}>
              Will appear in <strong>{navGroups.find((g) => g.id === selectedGroupId)?.sip_label}</strong>, position{" "}
              {navPosition + 1} of {groupModules.length + 1}, as "{navLabel || table.label}".
            </div>
          ) : (
            <div style={styles.fieldHint}>
              Not placed in the sidebar yet — you can add it later from Edit → Navigation.
            </div>
          )}
        </div>
      </div>
    );
  };

  const wizardStepIdx = WIZARD_STEPS.findIndex((s) => s.key === step);

  return (
    <div style={styles.container}>
      <div style={styles.toolbar}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={styles.headerIcon}>
            <Database size={18} />
          </span>
          <div>
            <h2 style={{ margin: 0 }}>{isNew ? "Create Table" : `Edit Table: ${table.label || table.name}`}</h2>
            <div style={styles.subtitle}>
              {isNew ? "Design the table, its fields, and who can access it." : "Manage fields and access control for this table."}
            </div>
          </div>
        </div>
        <div style={styles.toolbarActions}>
          {!isNew && (
            <button onClick={deleteTable} style={styles.btnDangerPrimary} type="button">
              <Trash2 size={14} /> Delete Table
            </button>
          )}
          {!isNew && (
            <button
              onClick={saveTable}
              disabled={isSaving}
              style={{ ...styles.btnPrimary, opacity: isSaving ? 0.85 : 1, cursor: isSaving ? "not-allowed" : "pointer" }}
              type="button"
            >
              {isSaving ? <Loader2 size={14} style={{ animation: "lv-spin 0.8s linear infinite" }} /> : <Save size={14} />}
              {isSaving ? "Saving..." : "Save"}
            </button>
          )}
        </div>
      </div>

      {isNew ? (
        <div style={styles.stepper}>
          {WIZARD_STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = s.key === step;
            const isDone = wizardStepIdx > i;
            return (
              <React.Fragment key={s.key}>
                <button
                  type="button"
                  onClick={() => (i <= wizardStepIdx || canLeaveBasics()) && setStep(s.key)}
                  style={{
                    ...styles.stepPill,
                    ...(isActive ? styles.stepPillActive : {}),
                    ...(isDone ? styles.stepPillDone : {}),
                  }}
                >
                  <span style={styles.stepIconWrap}>{isDone ? <Check size={12} /> : <Icon size={12} />}</span>
                  {s.label}
                </button>
                {i < WIZARD_STEPS.length - 1 && <span style={styles.stepConnector} aria-hidden />}
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        <div style={styles.tabBar}>
          {EDIT_TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === activeTab;
            const count = t.key === "fields" ? fields.length : aclRules.length;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                style={{ ...styles.tabBtn, ...(isActive ? styles.tabBtnActive : {}) }}
              >
                <Icon size={14} /> {t.label} <span style={styles.tabCount}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {errors.length > 0 && (
        <div style={styles.errorBox}>
          <strong>Fix the following issues:</strong>
          <ul style={{ margin: "6px 0 0 18px" }}>
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {isNew ? (
        <>
          {step === "basics" && renderBasicsPanel()}
          {step === "fields" && renderFieldsPanel()}
          {step === "access" && renderAccessPanel()}
          {step === "navigation" && renderNavigationPanel()}
          {step === "review" && renderReviewPanel()}

          <div style={styles.wizardFooter}>
            <button
              type="button"
              onClick={() => setStep(WIZARD_STEPS[Math.max(0, wizardStepIdx - 1)].key)}
              disabled={wizardStepIdx === 0}
              style={{ ...styles.btnGhost, opacity: wizardStepIdx === 0 ? 0.5 : 1 }}
            >
              <ChevronLeft size={14} /> Back
            </button>
            {wizardStepIdx === WIZARD_STEPS.length - 1 ? (
              <button
                type="button"
                onClick={saveTable}
                disabled={isSaving}
                style={{ ...styles.btnPrimary, opacity: isSaving ? 0.85 : 1 }}
              >
                {isSaving ? <Loader2 size={14} style={{ animation: "lv-spin 0.8s linear infinite" }} /> : <CircleCheck size={14} />}
                {isSaving ? "Creating..." : "Create Table"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (step === "basics" && !canLeaveBasics()) return;
                  setStep(WIZARD_STEPS[wizardStepIdx + 1].key);
                }}
                style={styles.btnPrimary}
              >
                Next <ChevronRight size={14} />
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          {activeTab === "fields" && renderFieldsPanel()}
          {activeTab === "access" && renderAccessPanel()}
          {activeTab === "navigation" && renderNavigationPanel()}
        </>
      )}

      <div style={{ height: 20 }} />
    </div>
  );
};

const styles = {
  container: {
    padding: 22,
    maxWidth: 1480,
    width: "min(96vw, 1480px)",
    margin: "0 auto",
    backgroundColor: "var(--bg, #f7faf9)",
  },

  toolbar: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    background: "var(--bg, #f7faf9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 0 16px",
    borderBottom: "1px solid var(--line, #dfe7e5)",
    marginBottom: 16,
  },
  headerIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 10,
    background: "var(--brand-wash, #e6f4f1)",
    color: "var(--brand-dark, #085f54)",
  },
  toolbarActions: { display: "flex", gap: 10 },
  subtitle: { color: "var(--muted, #5c6d6f)", fontSize: 13, marginTop: 4 },

  stepper: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 18,
    flexWrap: "wrap",
  },
  stepPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "8px 14px",
    borderRadius: 999,
    border: "1px solid var(--line, #dfe7e5)",
    background: "#fff",
    color: "var(--muted, #5c6d6f)",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  },
  stepPillActive: {
    borderColor: "var(--brand, #0d7d6f)",
    background: "var(--brand, #0d7d6f)",
    color: "#fff",
  },
  stepPillDone: {
    borderColor: "var(--brand, #0d7d6f)",
    color: "var(--brand-dark, #085f54)",
    background: "var(--brand-wash, #e6f4f1)",
  },
  stepIconWrap: { display: "inline-flex" },
  stepConnector: {
    width: 20,
    height: 1,
    background: "var(--line, #dfe7e5)",
  },

  tabBar: {
    display: "flex",
    gap: 6,
    marginBottom: 18,
    borderBottom: "1px solid var(--line, #dfe7e5)",
  },
  tabBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "10px 14px",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "transparent",
    color: "var(--muted, #5c6d6f)",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  },
  tabBtnActive: {
    color: "var(--brand-dark, #085f54)",
    borderBottomColor: "var(--brand, #0d7d6f)",
  },
  tabCount: {
    fontSize: 11,
    fontWeight: 800,
    padding: "1px 6px",
    borderRadius: 999,
    background: "var(--card-2, #f1f5f4)",
    color: "var(--ink-2, #1e2e30)",
  },

  wizardFooter: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 20,
    paddingTop: 16,
    borderTop: "1px dashed var(--line, #dfe7e5)",
  },

  basicsCard: {
    border: "1px solid var(--line, #dfe7e5)",
    borderRadius: 14,
    padding: 18,
    background: "#fff",
    boxShadow: "var(--shadow-xs, 0 1px 2px rgba(9,20,19,.05))",
  },

  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 18,
  },
  label: { fontWeight: 700, display: "block", marginBottom: 6, fontSize: 13, color: "var(--ink-2, #1e2e30)" },
  fieldHint: { fontSize: 12, color: "var(--muted, #5c6d6f)", marginTop: 6 },
  inputLg: {
    width: "100%",
    padding: 12,
    borderRadius: 10,
    border: "1px solid var(--line, #dfe7e5)",
    fontSize: 14,
    background: "#fff",
    boxSizing: "border-box",
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 8,
    marginBottom: 12,
  },

  infoBanner: {
    display: "flex",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 10,
    background: "var(--brand-wash, #e6f4f1)",
    color: "var(--brand-dark, #085f54)",
    fontSize: 13,
    marginBottom: 14,
  },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    padding: "32px 16px",
    color: "var(--muted, #5c6d6f)",
    fontSize: 13,
    border: "1px dashed var(--line, #dfe7e5)",
    borderRadius: 12,
    background: "#fff",
  },

  fieldCard: {
    border: "1px solid var(--line, #dfe7e5)",
    borderRadius: 14,
    padding: 14,
    background: "#fff",
    marginBottom: 14,
    boxShadow: "var(--shadow-xs, 0 1px 2px rgba(9,20,19,.05))",
  },

  auditRow: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 },
  badge: {
    background: "var(--card-2, #f1f5f4)",
    border: "1px solid var(--line, #dfe7e5)",
    color: "var(--ink-2, #1e2e30)",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 12,
  },

  fieldMainRow: {
    display: "grid",
    gridTemplateColumns: "1.2fr 1.2fr 1.4fr 1.2fr",
    gap: 12,
    alignItems: "center",
  },
  fieldSecondRow: {
    marginTop: 12,
    display: "grid",
    gridTemplateColumns: "1.2fr 1.4fr 1.2fr auto",
    gap: 12,
    alignItems: "end",
  },
  helpLabel: { fontSize: 11, color: "var(--muted, #5c6d6f)", marginBottom: 6, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" },

  fieldNameWrap: { display: "flex", alignItems: "center", gap: 8 },
  fieldTypeIconWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
    minWidth: 26,
    borderRadius: 7,
    background: "var(--brand-wash, #e6f4f1)",
    color: "var(--brand-dark, #085f54)",
    flexShrink: 0,
  },

  inputMd: {
    padding: 10,
    border: "1px solid var(--line, #dfe7e5)",
    borderRadius: 10,
    fontSize: 14,
    width: "100%",
    background: "#fff",
    boxSizing: "border-box",
  },
  inputSm: {
    padding: 8,
    border: "1px solid var(--line, #dfe7e5)",
    borderRadius: 10,
    fontSize: 13,
    background: "#fff",
  },

  checkboxGroup: { display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" },
  checkboxLabel: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink-2, #1e2e30)" },
  checkbox: { transform: "scale(1.1)", cursor: "pointer", accentColor: "var(--brand, #0d7d6f)" },

  inlineLinkIcon: { cursor: "pointer", color: "var(--brand, #0d7d6f)", display: "inline-flex" },

  // Buttons
  btn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid var(--line, #dfe7e5)",
    background: "#fff",
    color: "var(--ink-2, #1e2e30)",
    padding: "8px 12px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
  },
  btnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    backgroundColor: "var(--brand, #0d7d6f)",
    color: "#fff",
    padding: "10px 16px",
    border: "1px solid var(--brand, #0d7d6f)",
    borderRadius: 10,
    fontWeight: 700,
    cursor: "pointer",
  },
  btnPrimarySm: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    backgroundColor: "var(--brand, #0d7d6f)",
    color: "#fff",
    padding: "6px 10px",
    border: "1px solid var(--brand, #0d7d6f)",
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
  },
  btnGhost: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid var(--line, #dfe7e5)",
    background: "#fff",
    color: "var(--ink-2, #1e2e30)",
    padding: "10px 16px",
    borderRadius: 10,
    fontWeight: 700,
    cursor: "pointer",
  },
  btnGhostSm: {
    border: "1px solid var(--line, #dfe7e5)",
    background: "#fff",
    color: "var(--ink-2, #1e2e30)",
    padding: "6px 10px",
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 12,
    cursor: "pointer",
  },
  btnDangerPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    backgroundColor: "var(--danger, #dc2626)",
    color: "#fff",
    padding: "10px 14px",
    border: "1px solid var(--danger, #dc2626)",
    borderRadius: 10,
    fontWeight: 700,
    cursor: "pointer",
  },
  btnSuccess: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    backgroundColor: "var(--brand, #0d7d6f)",
    color: "#fff",
    padding: "9px 14px",
    border: "1px solid var(--brand, #0d7d6f)",
    borderRadius: 10,
    fontWeight: 700,
    cursor: "pointer",
  },
  btnDanger: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "var(--bad-wash, #fdecec)",
    border: "1px solid transparent",
    color: "var(--danger, #dc2626)",
    padding: "8px 12px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
  },
  btnDangerOutline: {
    background: "#fff",
    border: "1px solid #fecaca",
    color: "#b91c1c",
    padding: "6px 10px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 600,
  },
  iconBtnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--brand-wash, #e6f4f1)",
    border: "1px solid transparent",
    color: "var(--brand-dark, #085f54)",
    borderRadius: 8,
    padding: "7px 9px",
    cursor: "pointer",
  },
  iconBtnDanger: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--bad-wash, #fdecec)",
    border: "1px solid transparent",
    color: "var(--danger, #dc2626)",
    borderRadius: 8,
    padding: "7px 9px",
    cursor: "pointer",
  },
  errorBox: {
    background: "var(--bad-wash, #fdecec)",
    border: "1px solid #f5b5b5",
    color: "#7f1d1d",
    padding: "10px 12px",
    borderRadius: 10,
    marginBottom: 12,
  },

  advancedPanel: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    border: "1px dashed var(--line, #dfe7e5)",
    background: "var(--card-2, #f1f5f4)",
  },
  advancedGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
  },
  advLabel: { fontSize: 12, color: "var(--muted, #5c6d6f)", marginBottom: 6 },

  choiceWrap: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    border: "1px dashed var(--line, #dfe7e5)",
    background: "var(--card-2, #f1f5f4)",
  },
  choiceHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  csvPanel: {
    display: "grid",
    gap: 8,
    padding: 10,
    marginBottom: 10,
    borderRadius: 10,
    border: "1px solid var(--line, #dfe7e5)",
    background: "#fff",
  },
  csvTextarea: {
    width: "100%",
    minHeight: 90,
    padding: 10,
    borderRadius: 8,
    border: "1px solid var(--line, #dfe7e5)",
    fontFamily: "monospace",
    fontSize: 13,
    boxSizing: "border-box",
    resize: "vertical",
  },
  choiceGridHeader: {
    display: "grid",
    gridTemplateColumns: "110px 1fr 1fr 70px 100px",
    gap: 8,
    padding: "6px 8px",
    fontWeight: 700,
    fontSize: 12,
    color: "var(--ink-2, #1e2e30)",
    background: "#fff",
    borderRadius: 10,
    marginBottom: 6,
  },
  choiceEmpty: { color: "var(--muted, #5c6d6f)", fontStyle: "italic", padding: "4px 2px" },
  choiceRow: {
    display: "grid",
    gridTemplateColumns: "110px 1fr 1fr 70px 100px",
    gap: 8,
    alignItems: "center",
    padding: "6px 8px",
  },
  choiceInput: {
    padding: 9,
    border: "1px solid var(--line, #dfe7e5)",
    borderRadius: 10,
    fontSize: 13,
    boxSizing: "border-box",
  },

  // Access control
  aclCard: {
    border: "1px solid var(--line, #dfe7e5)",
    borderRadius: 14,
    padding: 14,
    background: "#fff",
    marginBottom: 14,
    boxShadow: "var(--shadow-xs, 0 1px 2px rgba(9,20,19,.05))",
  },
  aclCardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 10,
  },
  aclRoleWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  unsavedPill: {
    fontSize: 11,
    fontWeight: 800,
    padding: "2px 8px",
    borderRadius: 999,
    background: "var(--warn-wash, #fef3e2)",
    color: "var(--warn, #d97706)",
    whiteSpace: "nowrap",
  },
  methodRow: { display: "flex", gap: 6, flexWrap: "wrap" },
  methodChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid var(--line, #dfe7e5)",
    background: "#fff",
    color: "var(--muted, #5c6d6f)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  methodChipActive: {
    background: "var(--brand, #0d7d6f)",
    borderColor: "var(--brand, #0d7d6f)",
    color: "#fff",
  },
  aclSummaryLine: {
    marginTop: 10,
    fontSize: 12,
    color: "var(--muted, #5c6d6f)",
  },

  // Review
  reviewSection: { marginBottom: 18 },
  reviewSectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontWeight: 800,
    fontSize: 13,
    color: "var(--ink, #0f1a1c)",
    marginBottom: 8,
  },
  reviewLine: { fontSize: 13, color: "var(--ink-2, #1e2e30)", marginBottom: 4 },
  reviewFieldGrid: { display: "flex", flexWrap: "wrap", gap: 6 },
  reviewFieldChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 9px",
    borderRadius: 999,
    background: "var(--card-2, #f1f5f4)",
    color: "var(--ink-2, #1e2e30)",
    fontSize: 12,
    fontWeight: 600,
  },
  requiredDot: {
    width: 5,
    height: 5,
    borderRadius: 999,
    background: "var(--danger, #dc2626)",
    marginLeft: 2,
  },

  // Navigation placement
  navConfigGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBottom: 16,
  },
  navPreviewWrap: { marginTop: 4 },
  navPreviewHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    color: "var(--muted, #5c6d6f)",
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  navPreviewShell: {
    background: "linear-gradient(180deg, #111827 0%, #0f172a 100%)",
    borderRadius: 14,
    padding: 10,
    display: "grid",
    gap: 4,
    maxHeight: 360,
    overflowY: "auto",
    boxShadow: "0 10px 28px rgba(9,20,19,.18)",
  },
  navPreviewItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    borderRadius: 9,
    color: "#e5e7eb",
    fontSize: 13,
    fontWeight: 600,
  },
  navPreviewIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    minWidth: 22,
    borderRadius: 7,
    background: "rgba(255,255,255,0.06)",
    color: "#99f6e4",
  },
  navPreviewItemNew: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 10px",
    borderRadius: 9,
    background: "rgba(20,184,166,0.16)",
    border: "1px dashed rgba(45,212,191,0.6)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
  },
  navPreviewIconNew: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    minWidth: 22,
    borderRadius: 7,
    background: "linear-gradient(180deg, #5eead4 0%, #2dd4bf 100%)",
    color: "#04211d",
  },
  newBadge: {
    fontSize: 10,
    fontWeight: 800,
    padding: "2px 6px",
    borderRadius: 999,
    background: "#2dd4bf",
    color: "#04211d",
    letterSpacing: "0.04em",
  },
  navPreviewMoveBtns: { display: "flex", gap: 4 },
  navMoveBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.06)",
    color: "#e5e7eb",
    cursor: "pointer",
  },
  navPreviewEmpty: {
    padding: "6px 10px",
    fontSize: 12,
    color: "rgba(229,231,235,0.5)",
    fontStyle: "italic",
  },
};

export default TableFormView;
